<script>
import {mapActions, mapGetters, mapState} from "vuex";
import BildungsportalContact from "@/components/ui/Bildungsportal/BildungsportalContact.vue";
import RightColumnGroup from "@/components/layout/RightColumnGroup.vue";
import SectionContent from "@/components/ui/Bildungsportal/SectionContent.vue";
import { metricEventTypes, metricParents } from "@/lib";
import RichText from "@/components/ui/RichText.vue";


export default {
  name: "BildungsMaterialDetails",
  data: () => ({
    baseUrl: process.env.VUE_APP_API_BASE_URL,
    expansionModel: [],
  }),
  components: {
    RichText,
    SectionContent,
    RightColumnGroup,
    BildungsportalContact,
  },
  async mounted() {
    if(!this.material) {
      await this.loadMaterialById(this.$route.params.id);
    }
    await this.postHeartbeat({
      name: this.material.Titel,
      type: metricEventTypes.PAGE_VISIT,
      parent: metricParents.BILDUNGSPORTAL,
      data: {
        bildungsportalMetric: true,
        materialTyp: this.material.typ.Name,
        materialThemas: this.material.Themas.map(t => t.Name)
      }
    })
  },
  methods: {
    ...mapActions("bildungsportal", ["loadMaterialien", "loadMaterialById"]),
    ...mapActions("user", ["setShowBildungsportal"]),
    ...mapActions("metrics", ["postHeartbeat"]),
    scrollToForm() {
      const contactForm = document.getElementById("contact-form");

      window.scrollTo({
        top: contactForm.offsetTop,
        left: 0,
        behavior: "smooth"
      })
    }
  },
  computed: {
    ...mapState("bildungsportal", ["contactPerson"]),
    ...mapGetters("user", ["showBildungsportal"]),
    material() {
      return this.$store.getters["bildungsportal/getMaterialById"](parseInt(this.$route.params.id));
    },
    headerUrl() {
      return this.baseUrl + this.material?.Header?.url;
    },
    subjects() {
      return this.material?.Schulfaecher.map(f => f.Name).join(", ");
    },
    classes() {
      return this.material?.Jahrgangsstufen.map(s => s.Bezeichnung).join(", ");
    },
    showContactForm() {
      return this.material?.KontaktformularAnzeigen
    },
    hideDetailsBox() {
      return this.material.Schulfaecher.length === 0
          && !this.material.Dauer
          && this.material.Jahrgangsstufen.length === 0
          && !this.material.BNE_Ziele;
    },
    sanitizedYoutubeIframe() {
      if(!this.material.YoutubeIFrame) return undefined;
      return this.material.YoutubeIFrame.replace(/<iframe\b[^>]*>/i, tag => {
        // 1) drop width/height attributes
        let t = tag.replace(/\s+(?:width|height)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');

        // 2) add or merge style (remove existing width/aspect-ratio to avoid conflicts)
        if (/style\s*=/.test(t)) {
          t = t.replace(/style\s*=\s*(['"])([\s\S]*?)\1/i, (m, q, css) => {
            css = css.replace(/(?:^|;)\s*(?:width|aspect-ratio)\s*:[^;]*;?/gi, '').trim();
            if (css && !css.endsWith(';')) css += ';';
            return `style=${q}${css ? css + ' ' : ''}width: 100%; aspect-ratio: 16/9;${q}`;
          });
        } else {
          t = t.replace(/>$/, ' style="width: 100%; aspect-ratio: 16/9;">');
        }
        return t;
      });
    }
  }
}
</script>

<template>
  <v-row no-gutters class="min-height-100">
    <v-col cols="12" md="8" class="new-wrapper">
      <template v-if="material">
        <v-img v-if="material.Header" :src="headerUrl" style="height: 232px" />
        <div class="px-5 picture-caption" v-if="material.Header && material.Header.caption">
          {{ material.Header.caption }}
        </div>
        <div class="pt-7 px-5 pb-2">
          <v-chip v-if="!material.published_at" x-small label color="warning" class="material-typ-badge no-hover mr-2">ENTWURF</v-chip>
          <v-chip x-small label color="grey lighten-1" class="material-typ-badge no-hover">{{ material.typ.Name }}</v-chip>
          <div class="custom-header-text mt-2">{{ material.Titel }}</div>
          <p>{{ material.Subtitel}}</p>
        </div>
        <div class="seats-heads-up py-10 mb-2" v-if="material.Seats && material.Seats > 0 && material.StoererAnzeigen">
          <b>Begrenztes Kontingent - <span style="color: #00874B">Noch {{ material.Seats}} verfügbar</span></b>
          <v-btn color="primary" @click="scrollToForm">Jetzt beanspruchen</v-btn>
        </div>
          <rich-text class="md-text px-5" :source="material.Beschreibung"></rich-text>
          <div class="details-box px-5" v-if="!hideDetailsBox">
            <table class="details-table">
              <tbody>
                <tr v-if="this.subjects">
                  <td>Fächerzuordnung</td>
                  <th>{{ subjects }}</th>
                </tr>
                <tr v-if="this.material.Dauer">
                  <td>Dauer des Kurses</td>
                  <th> {{ material.Dauer }}</th>
                </tr>
                <tr v-if="this.classes">
                  <td>Jahrgangsstufe und Klasse</td>
                  <th> {{ classes }}</th>
                </tr>
                <tr v-if="this.material.BNE_Ziele">
                  <td>BNE-Ziele</td>
                  <th>{{ material.BNE_Ziele }}</th>
                </tr>
              </tbody>
            </table>
          </div>
        <div class="d-flex" v-if="sanitizedYoutubeIframe" v-html="sanitizedYoutubeIframe" />
        <div class="pa-5">
          <template v-if="!material.AkkordeonAusblenden">
          <v-expansion-panels class="pb-5" v-model="expansionModel" multiple accordion>
            <v-expansion-panel v-for="(inhalt, index) of material.MaterialInhalt" :key="index">
              <v-expansion-panel-header class="primary-text font-weight-bold">{{ inhalt.Titel }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <section-content v-for="section of inhalt.Section" :key="section.id" v-bind="section" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          </template>
          <template v-else>
            <div v-for="inhalt of material.MaterialInhalt" :key="inhalt.id" class="mb-10">
              <p v-if="inhalt.Titel" class="font-weight-bold text-h6 primary-text">{{ inhalt.Titel }}</p>
              <section-content v-for="section of inhalt.Section" :key="section.id" v-bind="section" />
            </div>
          </template>
          <template v-if="showContactForm">
            <bildungsportal-contact id="contact-form" v-if="material.typ.Name !== 'Meldungen'" :material-id="material.id" :material-name="material.Titel" />
            <bildungsportal-contact id="contact-form" v-else />
          </template>
        </div>
      </template>
    </v-col>
    <v-col
        cols="12" md="4"
        class="contact_container pl-4"
    >
      <RightColumnGroup v-if="material && (material.Ansprechpartner.length > 0 || material.links.length > 0)"
                        :content-object="{ personsInCharge: material.Ansprechpartner, interestingLinks: material.links }" />
    </v-col>
  </v-row>
</template>

<style scoped>
.md-text,
p {
  font-size: 16px;
}

.details-table {
  text-align: left;
}
.details-table td {
  padding: 4px 25px 4px 0;
}

.details-box, .seats-heads-up {
  background: rgb(235, 217, 227);
}

.details-box {
  padding-top: 16px;
  padding-bottom: 16px;
}

.seats-heads-up {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
